/* Ordering: freeze the sheet on the server, make the 300 DPI print file,
   then carry on to the Printees upload page (who's ordering, turnaround). */

import { useEffect, useState } from 'react';
import { formatMoney, formatDims } from '@gsb/core';
import { useStore, order, refreshSubmission } from '../store/store';
import { Dialog, Press } from '../ui/primitives';
import { Icon } from '../ui/icons';
import type { SubmissionInfo } from '../api';

export function OrderFlow({ onClose }: { onClose: () => void }) {
  const [sub, setSub] = useState<SubmissionInfo | null>(null);
  const [failed, setFailed] = useState(false);
  const contact = useStore(s => s.config?.links.contact || '/contact');

  useEffect(() => {
    let stop = false;
    (async () => {
      const s = await order();
      if (stop) return;
      if (!s) { onClose(); return; }
      setSub(s);
      let cur = s;
      while (!stop && cur.status === 'rendering') {
        await new Promise(r => setTimeout(r, 900));
        try { const n = await refreshSubmission(); if (n) { cur = n; if (!stop) setSub(n); } } catch { /* keep trying */ }
      }
      if (cur.status === 'failed') setFailed(true);
    })();
    return () => { stop = true; };
  }, []);

  if (!sub) return null;
  if (failed || sub.status === 'failed') {
    return (
      <Dialog title="We couldn’t make the print file" onClose={onClose}>
        <span className="kicker">Something went wrong</span>
        <h2>We couldn’t make your print file</h2>
        <p className="muted">That’s on us, not you. Your gangsheet is saved. Try again in a minute, or send us a message and we’ll sort it.</p>
        <div className="dialog-actions">
          <a className="btn btn-line btn-sm" href={contact}>Contact us</a>
          <button className="btn btn-primary btn-sm" onClick={onClose}>Close</button>
        </div>
      </Dialog>
    );
  }
  if (sub.status === 'rendering') {
    const pct = Math.round(sub.progress * 100);
    return (
      <div className="wait" role="alertdialog" aria-label="Creating your print-ready file">
        <div className="wait-card">
          <Press />
          <h2>Creating your print-ready file…</h2>
          <p>Every design is placed from your original file at 300 DPI, with a transparent background.</p>
          <div className={'bar' + (pct < 1 ? ' is-indet' : '')}><i style={pct >= 1 ? { width: pct + '%' } : undefined} /></div>
          <p className="tiny muted num">{pct >= 1 ? pct + '%' : 'Starting…'}</p>
        </div>
      </div>
    );
  }
  const p = sub.price;
  return (
    <Dialog title="Your gangsheet is print-ready" onClose={onClose} wide>
      <span className="kicker">Print-ready · Kua rite</span>
      <h2>Your gangsheet is ready to order</h2>
      <p className="muted small">We’ve checked it and made the 300 DPI print file. Next, tell us who’s ordering and pick your turnaround on printees.co.nz. We confirm the price before anything is charged.</p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, margin: '16px 0', maxHeight: 340, overflow: 'auto' }}>
        {sub.previews.map(pv => {
          const s = sub.sheets[pv.sheet - 1];
          const f = sub.files.find(x => x.sheet === pv.sheet);
          return (
            <figure key={pv.sheet} style={{ margin: 0 }}>
              <div style={{ background: 'var(--printees-checker)', borderRadius: 10, padding: 6, display: 'flex', justifyContent: 'center' }}>
                <img src={pv.url} alt={'Sheet ' + pv.sheet} style={{ maxHeight: 260, width: 'auto', objectFit: 'contain' }} />
              </div>
              <figcaption className="small" style={{ marginTop: 6 }}>
                <b>Sheet {pv.sheet}</b> · {s ? formatDims(s.widthMm, s.lengthMm, 'cm') + ' · ' + s.items + ' transfers' : ''}
                {f && <span className="muted tiny" style={{ display: 'block' }}>{f.widthPx} × {f.heightPx} px · {f.dpi} DPI · transparent PNG</span>}
              </figcaption>
            </figure>
          );
        })}
      </div>
      <div className="callout callout-good"><Icon name="okCircle" /><div><b>{formatMoney(p.total)}</b> incl. {p.taxLabel}{sub.priceRush ? ' · Rush ' + formatMoney(sub.priceRush.total) : ''}. Prices are worked out on our side from your finished sheet.</div></div>
      <div className="dialog-actions" style={{ justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {sub.files.map(f => (
            <a key={f.sheet} className="btn btn-line btn-sm" href={f.url} download={f.name}><Icon name="download" />{sub.files.length > 1 ? 'Sheet ' + f.sheet : 'Download print file'}</a>
          ))}
        </div>
        <a className="btn btn-primary" href={sub.handoffUrl}>Continue to order <Icon name="arrowRight" className="ico-arrow" /></a>
      </div>
    </Dialog>
  );
}
